// Aria's playback processor: one node plays every chunk of a reply from a queue on the audio thread.

import { ASSISTANT_PLAYBACK_SAMPLE_RATE, getWorkletModuleUrl } from './audioUtils'

const PLAYER_PROCESSOR_NAME = 'assistant-player-processor'

/** Sent instead of samples on barge-in: everything still queued is dropped. */
export const PLAYER_FLUSH_MESSAGE = 'flush'

/** Exported for its test, which runs the processor outside a browser. */
export const PLAYER_PROCESSOR_SOURCE = `
class AssistantPlayerProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this.chunks = []
    this.offset = 0
    this.port.onmessage = (event) => {
      if (event.data === '${PLAYER_FLUSH_MESSAGE}') {
        this.chunks = []
        this.offset = 0
        return
      }
      this.chunks.push(event.data)
    }
  }

  process(inputs, outputs) {
    const channel = outputs[0] && outputs[0][0]
    if (!channel) return true

    let written = 0
    while (written < channel.length && this.chunks.length > 0) {
      const chunk = this.chunks[0]
      const count = Math.min(channel.length - written, chunk.length - this.offset)
      channel.set(chunk.subarray(this.offset, this.offset + count), written)
      written += count
      this.offset += count
      if (this.offset < chunk.length) continue
      this.chunks.shift()
      this.offset = 0
    }
    // Nothing queued: silence until the next chunk arrives, never the last block again.
    channel.fill(0, written)
    return true
  }
}

registerProcessor('${PLAYER_PROCESSOR_NAME}', AssistantPlayerProcessor)
`

/** Playback runs at the model's own rate, so the player copies samples as they are; the browser resamples natively. */
export const ASSISTANT_PLAYBACK_CONTEXT_OPTIONS: AudioContextOptions = {
  sampleRate: ASSISTANT_PLAYBACK_SAMPLE_RATE,
  latencyHint: 'interactive'
}

/** Whether a playback context can run the player: it has AudioWorklet and plays at the model's rate. */
export const canRunPlayer = (context: AudioContext): boolean =>
  !!context.audioWorklet && context.sampleRate === ASSISTANT_PLAYBACK_SAMPLE_RATE

/** The player node for a context that can run it, or nothing when its module fails to load. */
export async function loadPlayerNode(context: AudioContext): Promise<AudioWorkletNode | undefined> {
  try {
    await context.audioWorklet.addModule(getWorkletModuleUrl(PLAYER_PROCESSOR_SOURCE))
    return new AudioWorkletNode(context, PLAYER_PROCESSOR_NAME, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1]
    })
  } catch {
    return undefined
  }
}
